// Desktop top bar: workspace › project breadcrumb on the left, env + executor
// pickers on the right. These selectors used to live inside the request
// builder — pulling them up here makes them workspace-wide instead of
// per-request, matching how teams actually think about "which env am I
// hitting right now?"

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type Environment, type Project, type Workspace } from "../lib/api";
import ScopeSwitcher from "./ScopeSwitcher";
import { EnvDot } from "../pages/Environments";

interface Props {
  workspaces: Workspace[];
  activeWorkspace: Workspace;
  projects: Project[];
  activeProject: Project | null;
  onManageEnvs: () => void;
  onManageAgents: () => void;
}

export type Executor = "worker" | "agent";

// localStorage keys per workspace so a user juggling multiple projects
// keeps their choice across reloads.
const execKey = (wid: string) => `rest.executor:${wid}`;
const agentKey = (wid: string) => `rest.agent:${wid}`;
const envKey = (wid: string) => `rest.env:${wid}`;

export function readExecutor(workspaceId: string): Executor {
  try {
    const v = localStorage.getItem(execKey(workspaceId));
    return v === "agent" ? "agent" : "worker";
  } catch { return "worker"; }
}
export function writeExecutor(workspaceId: string, v: Executor): void {
  try { localStorage.setItem(execKey(workspaceId), v); } catch { /* ignore */ }
}
export function readAgentId(workspaceId: string): string {
  try { return localStorage.getItem(agentKey(workspaceId)) ?? ""; } catch { return ""; }
}
export function writeAgentId(workspaceId: string, id: string): void {
  try {
    if (id) localStorage.setItem(agentKey(workspaceId), id);
    else localStorage.removeItem(agentKey(workspaceId));
  } catch { /* ignore */ }
}
export function readEnvId(workspaceId: string): string {
  try { return localStorage.getItem(envKey(workspaceId)) ?? ""; } catch { return ""; }
}
export function writeEnvId(workspaceId: string, id: string): void {
  try {
    if (id) localStorage.setItem(envKey(workspaceId), id);
    else localStorage.removeItem(envKey(workspaceId));
  } catch { /* ignore */ }
}

export default function Topbar({ workspaces, activeWorkspace, projects, activeProject, onManageEnvs, onManageAgents }: Props) {
  const [sp, setSp] = useSearchParams();
  // URL wins, then localStorage. This means deep-links still override but the
  // selection survives navigation that drops the search param.
  const urlEnvId = sp.get("env") ?? "";
  const envId = urlEnvId || readEnvId(activeWorkspace.id);
  // If the URL has a fresh env id, mirror it to localStorage so it's the
  // default on the next page.
  useEffect(() => {
    if (urlEnvId) writeEnvId(activeWorkspace.id, urlEnvId);
  }, [urlEnvId, activeWorkspace.id]);

  const envsQ = useQuery({
    queryKey: ["environments", activeWorkspace.id],
    queryFn: () => api.environments.list(activeWorkspace.id),
  });
  const envs = envsQ.data?.environments ?? [];
  const activeEnv = envs.find((e) => e.id === envId) ?? null;
  // If the stored env id is stale (env was deleted, or we switched
  // workspaces), drop it so we don't keep referring to a missing id.
  useEffect(() => {
    if (envId && envsQ.isSuccess && !envs.some((e) => e.id === envId)) {
      writeEnvId(activeWorkspace.id, "");
    }
  }, [envId, envsQ.isSuccess, envs, activeWorkspace.id]);

  const agentsQ = useQuery({
    queryKey: ["agents", activeWorkspace.id],
    queryFn: () => api.agents.list(activeWorkspace.id),
  });

  const [executor, setExecutorState] = useState<Executor>(() => readExecutor(activeWorkspace.id));
  const [agentId, setAgentIdState] = useState<string>(() => readAgentId(activeWorkspace.id));
  // When the workspace changes, reload the stored values.
  useEffect(() => {
    setExecutorState(readExecutor(activeWorkspace.id));
    setAgentIdState(readAgentId(activeWorkspace.id));
  }, [activeWorkspace.id]);

  const setExecutor = (v: Executor) => {
    setExecutorState(v);
    writeExecutor(activeWorkspace.id, v);
  };
  const setAgentId = (v: string) => {
    setAgentIdState(v);
    writeAgentId(activeWorkspace.id, v);
  };
  const setEnv = (v: string) => {
    writeEnvId(activeWorkspace.id, v);
    const next = new URLSearchParams(sp);
    if (v) next.set("env", v); else next.delete("env");
    setSp(next, { replace: true });
  };

  const onlineAgents = (agentsQ.data?.agents ?? []).filter((a) => a.online);

  return (
    <header className="hidden sm:flex items-center gap-3 border-b border-border bg-surface px-3 py-1.5 text-sm">
      <ScopeSwitcher
        workspaces={workspaces}
        activeWorkspace={activeWorkspace}
        projects={projects}
        activeProject={activeProject}
      />
      <div className="ml-auto flex items-center gap-2">
        <EnvSelector envs={envs} active={activeEnv} onPick={setEnv} onManage={onManageEnvs} />
        <ExecutorSelector
          executor={executor}
          agentId={agentId}
          onlineAgents={onlineAgents}
          allAgents={agentsQ.data?.agents ?? []}
          onExecutor={setExecutor}
          onAgent={setAgentId}
          onManage={onManageAgents}
        />
      </div>
    </header>
  );
}

function EnvSelector({
  envs, active, onPick, onManage,
}: { envs: Environment[]; active: Environment | null; onPick: (id: string) => void; onManage: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-surface-2 border border-border"
        title={active ? `Environment: ${active.name}` : "Pick an environment"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <EnvDot color={active?.color ?? null} size={10} />
        <span className="text-xs">{active?.name ?? "No env"}</span>
        <CaretDown />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-50 text-sm w-56" role="menu">
          <button
            type="button"
            className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-2 text-left text-text-dim hover:text-text"
            onClick={() => { onPick(""); setOpen(false); }}
          >
            <EnvDot color={null} size={10} />
            <span className="flex-1">No env</span>
            {!active && <span className="chip bg-surface-3 muted-strong text-[10px]">current</span>}
          </button>
          {envs.length === 0 && (
            <div className="px-2 py-2 text-xs muted-strong italic">No environments yet.</div>
          )}
          {envs.map((e) => (
            <button
              key={e.id}
              type="button"
              className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-2 text-left"
              onClick={() => { onPick(e.id); setOpen(false); }}
            >
              <EnvDot color={e.color ?? null} size={10} />
              <span className="flex-1 truncate">{e.name}</span>
              {e.is_default && <span className="chip bg-surface-3 muted-strong text-[10px]">default</span>}
              {active?.id === e.id && <span className="chip bg-surface-3 muted-strong text-[10px]">current</span>}
            </button>
          ))}
          <div className="border-t border-border my-1" />
          <button
            type="button"
            className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-2 text-left text-text-dim hover:text-text"
            onClick={() => { onManage(); setOpen(false); }}
          >Manage…</button>
        </div>
      )}
    </div>
  );
}

function ExecutorSelector({
  executor, agentId, onlineAgents, allAgents, onExecutor, onAgent, onManage,
}: {
  executor: Executor;
  agentId: string;
  onlineAgents: Array<{ id: string; name: string; online: boolean }>;
  allAgents: Array<{ id: string; name: string; online: boolean }>;
  onExecutor: (v: Executor) => void;
  onAgent: (id: string) => void;
  onManage: () => void;
}) {
  const [open, setOpen] = useState(false);
  const currentAgent = allAgents.find((a) => a.id === agentId);
  const label = executor === "worker" ? "Worker" : (currentAgent?.name ?? "Pick agent…");
  const isReady = executor === "worker" || !!currentAgent?.online;
  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          "flex items-center gap-2 px-2 py-1 rounded-md hover:bg-surface-2 border " +
          (isReady ? "border-border" : "border-warn/40 text-warn")
        }
        aria-haspopup="menu"
        aria-expanded={open}
        title={executor === "agent" && !currentAgent?.online ? "Agent offline — pick another or start it." : "Where requests run"}
      >
        <ExecIcon kind={executor} ok={isReady} />
        <span className="text-xs">{label}</span>
        <CaretDown />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-50 text-sm w-64" role="menu">
          <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider muted-strong">Execute on</div>
          <button
            type="button"
            onClick={() => { onExecutor("worker"); setOpen(false); }}
            className={"w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-2 text-left " + (executor === "worker" ? "bg-surface-2" : "")}
          >
            <ExecIcon kind="worker" ok />
            <span className="flex-1">Cloudflare worker</span>
            {executor === "worker" && <span className="chip bg-surface-3 muted-strong text-[10px]">current</span>}
          </button>
          <button
            type="button"
            onClick={() => { onExecutor("agent"); setOpen(false); }}
            className={"w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-2 text-left " + (executor === "agent" ? "bg-surface-2" : "")}
          >
            <ExecIcon kind="agent" ok={onlineAgents.length > 0} />
            <span className="flex-1">Local agent</span>
            {executor === "agent" && <span className="chip bg-surface-3 muted-strong text-[10px]">current</span>}
          </button>
          {executor === "agent" && (
            <div className="border-t border-border mt-1 pt-1">
              <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider muted-strong">Agent</div>
              {allAgents.length === 0 && (
                <button
                  type="button"
                  onClick={() => { onManage(); setOpen(false); }}
                  className="w-full px-2 py-1 text-left muted-strong text-xs"
                >No agents — register one →</button>
              )}
              {allAgents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => { onAgent(a.id); setOpen(false); }}
                  className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-2 text-left"
                >
                  <span className={"h-2 w-2 rounded-full shrink-0 " + (a.online ? "bg-good" : "bg-text-faint")} />
                  <span className="flex-1 truncate">{a.name}</span>
                  {a.id === agentId && <span className="chip bg-surface-3 muted-strong text-[10px]">current</span>}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-border mt-1 pt-1">
            <button
              type="button"
              onClick={() => { onManage(); setOpen(false); }}
              className="w-full px-2 py-1 text-left text-text-dim hover:bg-surface-2 hover:text-text rounded"
            >Manage agents…</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExecIcon({ kind, ok }: { kind: Executor; ok: boolean }) {
  return (
    <span className={"h-5 w-5 grid place-items-center rounded " + (ok ? "text-text-dim" : "text-warn")}>
      {kind === "worker" ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 14a4 4 0 014-4 5 5 0 019.6 1.5A3.5 3.5 0 0118 18H7a3 3 0 01-2-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      )}
    </span>
  );
}
function CaretDown() {
  return (
    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0 text-text-faint">
      <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
